import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, GitBranch, User, Calendar, FileText, TrendingUp, TrendingDown, Minus, Edit } from 'lucide-react';
import api from 'services/api';
import { usePermissions } from 'hooks/usePermissions';

const ModalVersoesConciliacao = ({ 
  isOpen, 
  onClose, 
  idContaBancaria, 
  dataConciliacao,
  periodoInicio,
  periodoFim
}) => {
  const { hasPermission } = usePermissions();
  const podeEditarConciliacao = hasPermission('administrativo.edit');
  const [versoes, setVersoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [versoesSelecionadas, setVersoesSelecionadas] = useState([]);
  const [mostrarComparacao, setMostrarComparacao] = useState(false);
  const [versoesExpandidas, setVersoesExpandidas] = useState({});
  const [categoriaAtiva, setCategoriaAtiva] = useState({});

  const carregarVersoes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        id_conta_bancaria: idContaBancaria.toString(),
        data_conciliacao: dataConciliacao,
        periodo_inicio: periodoInicio,
        periodo_fim: periodoFim
      });

      const response = await api.get(`/n8n/administracao/conciliacao-bancaria/versoes?${params}`);

      if (response.data.success) {
        setVersoes(response.data.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar Versões:', err);
    } finally {
      setLoading(false);
    }
  }, [idContaBancaria, dataConciliacao, periodoInicio, periodoFim]);

  useEffect(() => {
    if (isOpen && idContaBancaria && dataConciliacao && periodoInicio && periodoFim) {
      carregarVersoes();
    } else {
      setVersoes([]);
      setVersoesSelecionadas([]);
      setMostrarComparacao(false);
    }
  }, [isOpen, idContaBancaria, dataConciliacao, periodoInicio, periodoFim, carregarVersoes]);

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      const date = new Date(data);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return data;
    }
  };

  const handleSelecionarVersao = (versaoId) => {
    setVersoesSelecionadas(prev => {
      if (prev.includes(versaoId)) {
        // Desmarcar
        return prev.filter(id => id !== versaoId);
      } else if (prev.length < 2) {
        // Adicionar (máximo 2)
        return [...prev, versaoId];
      } else {
        // Substituir a primeira seleção
        return [prev[1], versaoId];
      }
    });
  };

  const handleComparar = () => {
    if (versoesSelecionadas.length === 2) {
      setMostrarComparacao(true);
    }
  };

  const getVersaoSelecionada = (id) => {
    return versoes.find(v => v.id === id);
  };

  const compararValores = (valor1, valor2) => {
    if (valor1 === valor2) return 'igual';
    return valor1 > valor2 ? 'maior' : 'menor';
  };

  const abrirParaEdicao = (conciliacaoId) => {
    localStorage.setItem('conciliacao_editar_id', conciliacaoId.toString());
    window.dispatchEvent(new CustomEvent('navigate-to-conciliacao'));
    onClose();
  };

  const toggleVerContas = (versaoId) => {
    setVersoesExpandidas(prev => ({
      ...prev,
      [versaoId]: !prev[versaoId]
    }));
    // Definir categoria padrão como 'conciliadas' se não houver
    if (!categoriaAtiva[versaoId]) {
      setCategoriaAtiva(prev => ({
        ...prev,
        [versaoId]: 'conciliadas'
      }));
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarDataSimples = (data) => {
    if (!data) return '-';
    try {
      const date = new Date(data);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return data;
    }
  };

  const getTipoTransacao = (item) => {
    const tipo = (item.tipo || '').toUpperCase();
    if (tipo === 'CREDIT' || tipo === 'CRÉDITO') return { label: 'Crédito', cor: 'green' };
    if (tipo === 'DEBIT' || tipo === 'DÉBITO') return { label: 'Débito', cor: 'red' };
    return { label: 'N/A', cor: 'gray' };
  };

  const getCategoriaInfo = (categoria) => {
    const map = {
      'conciliadas': { label: 'Conciliadas', cor: 'blue' },
      'pendencias_api': { label: 'Pendências API', cor: 'yellow' },
      'pendencias_ofx': { label: 'Pendências OFX', cor: 'orange' },
      'duplicidades': { label: 'Duplicidades', cor: 'red' }
    };
    return map[categoria] || { label: categoria, cor: 'gray' };
  };

  if (!isOpen) return null;

  const versao1 = versoesSelecionadas[0] ? getVersaoSelecionada(versoesSelecionadas[0]) : null;
  const versao2 = versoesSelecionadas[1] ? getVersaoSelecionada(versoesSelecionadas[1]) : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div 
          className="fixed inset-0 bg-black/40 bg-opacity-75 transition-opacity" 
          onClick={onClose}
        />
        
        <div className="relative inline-block w-full max-w-6xl p-6 my-8 text-left align-middle bg-[color:var(--surface)] rounded-lg shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-[color:var(--border)]">
            <div>
              <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
                Versões da Conciliação
              </h3>
              <p className="text-sm text-[color:var(--text-muted)] mt-1">
                Selecione até 2 versões para comparar
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)] rounded-full hover:bg-[color:var(--surface-muted)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 text-[color:var(--accent)] animate-spin mx-auto mb-4" />
              <p className="text-[color:var(--text-muted)]">Carregando versões...</p>
            </div>
          ) : versoes.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-[color:var(--text-muted)] mx-auto mb-4" />
              <p className="text-[color:var(--text-muted)]">Nenhuma versão encontrada</p>
            </div>
          ) : (
            <div className="mt-6">
              {/* Lista de Versões */}
              {!mostrarComparacao && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-[color:var(--text-primary)] mb-3">
                    Selecione as versões para comparar:
                  </h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {versoes.map((versao) => {
                      const isSelecionada = versoesSelecionadas.includes(versao.id);
                      return (
                        <div
                          key={versao.id}
                          onClick={() => handleSelecionarVersao(versao.id)}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            isSelecionada
                              ? 'border-[color:var(--accent)] bg-[color:var(--surface)] ring-1 ring-[color:var(--accent)] shadow-sm'
                              : 'border-[color:var(--border)] bg-[color:var(--surface)] hover:border-[color:var(--accent)] hover:bg-[color:var(--surface-muted)]'
                          }`}
                        >
                          {/* Header: Checkbox, Versão, Data e Usuário */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3 flex-1">
                              <input
                                type="checkbox"
                                checked={isSelecionada}
                                onChange={() => handleSelecionarVersao(versao.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-5 w-5 text-[color:var(--accent)] rounded mt-0.5 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <GitBranch className="h-5 w-5 text-[color:var(--accent)]" />
                                  <span className="font-semibold text-lg text-[color:var(--text-primary)]">
                                    Versão {versao.versao}
                                  </span>
                                  {isSelecionada && (
                                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-[color:var(--accent)] text-white">
                                      Selecionada
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-[color:var(--text-muted)] ml-7">
                                  <div className="flex items-center gap-1.5">
                                    <Calendar className="h-4 w-4 text-[color:var(--text-muted)]" />
                                    <span>{formatarData(versao.created_at)}</span>
                                  </div>
                                  {versao.criado_por && (
                                    <div className="flex items-center gap-1.5">
                                      <User className="h-4 w-4 text-[color:var(--text-muted)]" />
                                      <span className="truncate max-w-[180px]" title={versao.criado_por.full_name || versao.criado_por.corporate_email}>
                                        {versao.criado_por.full_name || versao.criado_por.corporate_email}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {podeEditarConciliacao && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  abrirParaEdicao(versao.id);
                                }}
                                className="p-2 text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg hover:bg-[color:var(--surface)] flex items-center justify-center transition-colors"
                                title="Editar Conciliação"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                          </div>

                          {/* Métricas em Grid mais espaçado */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 ml-7">
                            <div className="bg-[color:var(--surface)] rounded-lg p-2.5 border border-[color:var(--border)]">
                              <div className="text-xs text-[color:var(--text-muted)] mb-1">Pendências API</div>
                              <div className="text-lg font-bold text-[color:var(--text-primary)]">
                                {versao.qtd_pendencias_api_depois}
                              </div>
                            </div>
                            <div className="bg-[color:var(--surface)] rounded-lg p-2.5 border border-[color:var(--border)]">
                              <div className="text-xs text-[color:var(--text-muted)] mb-1">Pendências OFX</div>
                              <div className="text-lg font-bold text-[color:var(--text-primary)]">
                                {versao.qtd_pendencias_ofx_depois}
                              </div>
                            </div>
                            <div className="bg-[color:var(--surface-muted)] rounded-lg p-2.5 border border-[color:var(--border)]">
                              <div className="text-xs text-[color:var(--accent)] mb-1">Conciliações Automáticas</div>
                              <div className="text-lg font-bold text-[color:var(--text-primary)]">
                                {versao.qtd_conciliadas_depois}
                              </div>
                            </div>
                            <div className="bg-[color:var(--surface-muted)] rounded-lg p-2.5 border border-[color:var(--border)]">
                              <div className="text-xs text-[color:var(--accent)] mb-1">Conciliações Manuais</div>
                              <div className="text-lg font-bold text-[color:var(--text-primary)]">
                                {versao.conciliacoes_manuais?.length || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {versoesSelecionadas.length > 0 && (
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-sm text-[color:var(--text-muted)]">
                        {versoesSelecionadas.length} de 2 Versões selecionadas
                      </p>
                      <button
                        onClick={handleComparar}
                        disabled={versoesSelecionadas.length !== 2}
                        className="px-4 py-2 bg-[color:var(--accent)] text-white rounded-lg hover:opacity-90 border border-transparent shadow-sm disabled:bg-[color:var(--surface-muted)] disabled:text-[color:var(--text-muted)] disabled:border-[color:var(--border)] disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        Comparar Versões
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Comparação Visual */}
              {mostrarComparacao && versao1 && versao2 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-[color:var(--text-primary)]">
                      Comparação entre Versões
                    </h4>
                    <button
                      onClick={() => {
                        setMostrarComparacao(false);
                        setVersoesSelecionadas([]);
                      }}
                      className="text-sm text-[color:var(--accent)] hover:text-[color:var(--text-primary)]"
                    >
                      Voltar à seleção
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Versão 1 */}
                    <div className="border border-[color:var(--border)] bg-[color:var(--surface)] rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4 pb-2 border-b border-[color:var(--border)]">
                        <div>
                          <div className="font-semibold text-[color:var(--text-primary)]">
                            Versão {versao1.versao}
                          </div>
                          <div className="text-xs text-[color:var(--text-muted)] mt-1">
                            {formatarData(versao1.created_at)}
                          </div>
                          {versao1.criado_por && (
                            <div className="flex items-center gap-1 text-xs text-[color:var(--text-muted)] mt-1">
                              <User className="h-3 w-3" />
                              <span className="truncate" title={versao1.criado_por.full_name || versao1.criado_por.corporate_email}>
                                {versao1.criado_por.full_name || versao1.criado_por.corporate_email}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]">
                          V{versao1.versao}
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Pendências API (Antes → Depois)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{versao1.qtd_pendencias_api_antes}</span>
                            <span className="text-[color:var(--text-muted)]">→</span>
                            <span className={`text-sm font-semibold ${
                              compararValores(versao1.qtd_pendencias_api_antes, versao1.qtd_pendencias_api_depois) === 'menor'
                                ? 'text-red-600'  // Aumentou = RUIM
                                : compararValores(versao1.qtd_pendencias_api_antes, versao1.qtd_pendencias_api_depois) === 'maior'
                                ? 'text-green-600'  // Diminuiu = BOM
                                : 'text-[color:var(--text-muted)]'
                            }`}>
                              {versao1.qtd_pendencias_api_depois}
                            </span>
                            {compararValores(versao1.qtd_pendencias_api_antes, versao1.qtd_pendencias_api_depois) === 'menor' && (
                              <TrendingUp className="h-4 w-4 text-red-600" />  // Aumentou = RUIM
                            )}
                            {compararValores(versao1.qtd_pendencias_api_antes, versao1.qtd_pendencias_api_depois) === 'maior' && (
                              <TrendingDown className="h-4 w-4 text-green-600" />  // Diminuiu = BOM
                            )}
                            {compararValores(versao1.qtd_pendencias_api_antes, versao1.qtd_pendencias_api_depois) === 'igual' && (
                              <Minus className="h-4 w-4 text-[color:var(--text-muted)]" />
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Pendências OFX (Antes → Depois)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{versao1.qtd_pendencias_ofx_antes}</span>
                            <span className="text-[color:var(--text-muted)]">→</span>
                            <span className={`text-sm font-semibold ${
                              compararValores(versao1.qtd_pendencias_ofx_antes, versao1.qtd_pendencias_ofx_depois) === 'menor'
                                ? 'text-red-600'  // Aumentou = RUIM
                                : compararValores(versao1.qtd_pendencias_ofx_antes, versao1.qtd_pendencias_ofx_depois) === 'maior'
                                ? 'text-green-600'  // Diminuiu = BOM
                                : 'text-[color:var(--text-muted)]'
                            }`}>
                              {versao1.qtd_pendencias_ofx_depois}
                            </span>
                            {compararValores(versao1.qtd_pendencias_ofx_antes, versao1.qtd_pendencias_ofx_depois) === 'menor' && (
                              <TrendingUp className="h-4 w-4 text-red-600" />  // Aumentou = RUIM
                            )}
                            {compararValores(versao1.qtd_pendencias_ofx_antes, versao1.qtd_pendencias_ofx_depois) === 'maior' && (
                              <TrendingDown className="h-4 w-4 text-green-600" />  // Diminuiu = BOM
                            )}
                            {compararValores(versao1.qtd_pendencias_ofx_antes, versao1.qtd_pendencias_ofx_depois) === 'igual' && (
                              <Minus className="h-4 w-4 text-[color:var(--text-muted)]" />
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Conciliações Automáticas</div>
                          <div className="text-lg font-semibold text-[color:var(--accent)]">
                            {versao1.qtd_conciliadas_depois}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Conciliações Manuais</div>
                          <div className="text-lg font-semibold text-[color:var(--accent)]">
                            {versao1.conciliacoes_manuais?.length || 0}
                          </div>
                        </div>

                        {versao1.criado_por && (
                          <div>
                            <div className="text-xs text-[color:var(--text-muted)] mb-1">Criado por</div>
                            <div className="text-sm text-[color:var(--text-primary)]">
                              {versao1.criado_por.full_name || versao1.criado_por.corporate_email}
                            </div>
                          </div>
                        )}

                        {/* Botão Ver Contas */}
                        <div className="pt-2 border-t">
                          <button
                            onClick={() => toggleVerContas(versao1.id)}
                            className="w-full px-3 py-2 text-xs font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg hover:bg-[color:var(--surface-muted)] transition-colors"
                          >
                            {versoesExpandidas[versao1.id] ? 'Ocultar contas' : 'Ver contas'}
                          </button>
                        </div>

                        {/* Visualização de Contas */}
                        {versoesExpandidas[versao1.id] && versao1.linhas_por_categoria && (
                          <div className="mt-4 pt-4 border-t">
                            {/* Tabs de Categorias */}
                            <div className="flex gap-1 mb-3 overflow-x-auto">
                              {['conciliadas', 'pendencias_api', 'pendencias_ofx', 'duplicidades'].map((cat) => {
                                const catInfo = getCategoriaInfo(cat);
                                const count = versao1.linhas_por_categoria[cat]?.length || 0;
                                const isActive = categoriaAtiva[versao1.id] === cat;
                                let activeClasses = 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] border border-[color:var(--border)]';
                                if (isActive) {
                                  if (catInfo.cor === 'blue') activeClasses = 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border border-[color:var(--border)]';
                                  else if (catInfo.cor === 'yellow') activeClasses = 'bg-yellow-100 text-yellow-700 border border-yellow-300';
                                  else if (catInfo.cor === 'orange') activeClasses = 'bg-orange-100 text-orange-700 border border-orange-300';
                                  else if (catInfo.cor === 'red') activeClasses = 'bg-red-100 text-red-700 border border-red-300';
                                }
                                return (
                                  <button
                                    key={cat}
                                    onClick={() => setCategoriaAtiva(prev => ({ ...prev, [versao1.id]: cat }))}
                                    className={`px-2 py-1 text-xs font-medium rounded whitespace-nowrap ${activeClasses} ${
                                      !isActive ? 'hover:bg-[color:var(--surface-muted)]' : ''
                                    }`}
                                  >
                                    {catInfo.label} ({count})
                                  </button>
                                );
                              })}
                            </div>

                            {/* Lista de Contas */}
                            <div className="max-h-64 overflow-y-auto space-y-2">
                              {versao1.linhas_por_categoria[categoriaAtiva[versao1.id] || 'conciliadas']?.length > 0 ? (
                                versao1.linhas_por_categoria[categoriaAtiva[versao1.id] || 'conciliadas'].slice(0, 20).map((item, idx) => {
                                  const tipoInfo = getTipoTransacao(item);
                                  const _catInfo = getCategoriaInfo(item.categoria || categoriaAtiva[versao1.id]);
                                  return (
                                    <div
                                      key={idx}
                                      className="p-2 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded text-xs"
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-[color:var(--text-primary)] truncate">
                                            {item.fornecedor || item.nome || 'Sem nome'}
                                          </div>
                                          <div className="text-[color:var(--text-muted)] mt-0.5">
                                            {formatarDataSimples(item.data)}
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                          <div className={`font-semibold ${
                                            tipoInfo.cor === 'green' ? 'text-green-600' :
                                            tipoInfo.cor === 'red' ? 'text-red-600' : 'text-[color:var(--text-muted)]'
                                          }`}>
                                            {formatarMoeda(item.valor)}
                                          </div>
                                          <div className="flex gap-1">
                                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                                              tipoInfo.cor === 'green' ? 'bg-green-100 text-[color:var(--text-primary)]' :
                                              tipoInfo.cor === 'red' ? 'bg-red-100 text-red-700' : 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
                                            }`}>
                                              {tipoInfo.label}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="text-center text-xs text-[color:var(--text-muted)] py-4">
                                  Nenhuma conta nesta categoria
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Versão 2 */}
                    <div className="border border-[color:var(--border)] bg-[color:var(--surface)] rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4 pb-2 border-b border-[color:var(--border)]">
                        <div>
                          <div className="font-semibold text-[color:var(--text-primary)]">
                            Versão {versao2.versao}
                          </div>
                          <div className="text-xs text-[color:var(--text-muted)] mt-1">
                            {formatarData(versao2.created_at)}
                          </div>
                          {versao2.criado_por && (
                            <div className="flex items-center gap-1 text-xs text-[color:var(--text-muted)] mt-1">
                              <User className="h-3 w-3" />
                              <span className="truncate" title={versao2.criado_por.full_name || versao2.criado_por.corporate_email}>
                                {versao2.criado_por.full_name || versao2.criado_por.corporate_email}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]">
                          V{versao2.versao}
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Pendências API (Antes → Depois)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{versao2.qtd_pendencias_api_antes}</span>
                            <span className="text-[color:var(--text-muted)]">→</span>
                            <span className={`text-sm font-semibold ${
                              compararValores(versao2.qtd_pendencias_api_antes, versao2.qtd_pendencias_api_depois) === 'menor'
                                ? 'text-red-600'  // Aumentou = RUIM
                                : compararValores(versao2.qtd_pendencias_api_antes, versao2.qtd_pendencias_api_depois) === 'maior'
                                ? 'text-green-600'  // Diminuiu = BOM
                                : 'text-[color:var(--text-muted)]'
                            }`}>
                              {versao2.qtd_pendencias_api_depois}
                            </span>
                            {compararValores(versao2.qtd_pendencias_api_antes, versao2.qtd_pendencias_api_depois) === 'menor' && (
                              <TrendingUp className="h-4 w-4 text-red-600" />  // Aumentou = RUIM
                            )}
                            {compararValores(versao2.qtd_pendencias_api_antes, versao2.qtd_pendencias_api_depois) === 'maior' && (
                              <TrendingDown className="h-4 w-4 text-green-600" />  // Diminuiu = BOM
                            )}
                            {compararValores(versao2.qtd_pendencias_api_antes, versao2.qtd_pendencias_api_depois) === 'igual' && (
                              <Minus className="h-4 w-4 text-[color:var(--text-muted)]" />
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Pendências OFX (Antes → Depois)</div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{versao2.qtd_pendencias_ofx_antes}</span>
                            <span className="text-[color:var(--text-muted)]">→</span>
                            <span className={`text-sm font-semibold ${
                              compararValores(versao2.qtd_pendencias_ofx_antes, versao2.qtd_pendencias_ofx_depois) === 'menor'
                                ? 'text-red-600'  // Aumentou = RUIM
                                : compararValores(versao2.qtd_pendencias_ofx_antes, versao2.qtd_pendencias_ofx_depois) === 'maior'
                                ? 'text-green-600'  // Diminuiu = BOM
                                : 'text-[color:var(--text-muted)]'
                            }`}>
                              {versao2.qtd_pendencias_ofx_depois}
                            </span>
                            {compararValores(versao2.qtd_pendencias_ofx_antes, versao2.qtd_pendencias_ofx_depois) === 'menor' && (
                              <TrendingUp className="h-4 w-4 text-red-600" />  // Aumentou = RUIM
                            )}
                            {compararValores(versao2.qtd_pendencias_ofx_antes, versao2.qtd_pendencias_ofx_depois) === 'maior' && (
                              <TrendingDown className="h-4 w-4 text-green-600" />  // Diminuiu = BOM
                            )}
                            {compararValores(versao2.qtd_pendencias_ofx_antes, versao2.qtd_pendencias_ofx_depois) === 'igual' && (
                              <Minus className="h-4 w-4 text-[color:var(--text-muted)]" />
                            )}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Conciliações Automáticas</div>
                          <div className="text-lg font-semibold text-[color:var(--accent)]">
                            {versao2.qtd_conciliadas_depois}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-[color:var(--text-muted)] mb-1">Conciliações Manuais</div>
                          <div className="text-lg font-semibold text-[color:var(--accent)]">
                            {versao2.conciliacoes_manuais?.length || 0}
                          </div>
                        </div>

                        {versao2.criado_por && (
                          <div>
                            <div className="text-xs text-[color:var(--text-muted)] mb-1">Criado por</div>
                            <div className="text-sm text-[color:var(--text-primary)]">
                              {versao2.criado_por.full_name || versao2.criado_por.corporate_email}
                            </div>
                          </div>
                        )}

                        {/* Botão Ver Contas */}
                        <div className="pt-2 border-t">
                          <button
                            onClick={() => toggleVerContas(versao2.id)}
                            className="w-full px-3 py-2 text-xs font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg hover:bg-[color:var(--surface-muted)] transition-colors"
                          >
                            {versoesExpandidas[versao2.id] ? 'Ocultar contas' : 'Ver contas'}
                          </button>
                        </div>

                        {/* Visualização de Contas */}
                        {versoesExpandidas[versao2.id] && versao2.linhas_por_categoria && (
                          <div className="mt-4 pt-4 border-t">
                            {/* Tabs de Categorias */}
                            <div className="flex gap-1 mb-3 overflow-x-auto">
                              {['conciliadas', 'pendencias_api', 'pendencias_ofx', 'duplicidades'].map((cat) => {
                                const catInfo = getCategoriaInfo(cat);
                                const count = versao2.linhas_por_categoria[cat]?.length || 0;
                                const isActive = categoriaAtiva[versao2.id] === cat;
                                let activeClasses = 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] border border-[color:var(--border)]';
                                if (isActive) {
                                  if (catInfo.cor === 'blue') activeClasses = 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border border-[color:var(--border)]';
                                  else if (catInfo.cor === 'yellow') activeClasses = 'bg-yellow-100 text-yellow-700 border border-yellow-300';
                                  else if (catInfo.cor === 'orange') activeClasses = 'bg-orange-100 text-orange-700 border border-orange-300';
                                  else if (catInfo.cor === 'red') activeClasses = 'bg-red-100 text-red-700 border border-red-300';
                                }
                                return (
                                  <button
                                    key={cat}
                                    onClick={() => setCategoriaAtiva(prev => ({ ...prev, [versao2.id]: cat }))}
                                    className={`px-2 py-1 text-xs font-medium rounded whitespace-nowrap ${activeClasses} ${
                                      !isActive ? 'hover:bg-[color:var(--surface-muted)]' : ''
                                    }`}
                                  >
                                    {catInfo.label} ({count})
                                  </button>
                                );
                              })}
                            </div>

                            {/* Lista de Contas */}
                            <div className="max-h-64 overflow-y-auto space-y-2">
                              {versao2.linhas_por_categoria[categoriaAtiva[versao2.id] || 'conciliadas']?.length > 0 ? (
                                versao2.linhas_por_categoria[categoriaAtiva[versao2.id] || 'conciliadas'].slice(0, 20).map((item, idx) => {
                                  const tipoInfo = getTipoTransacao(item);
                                  const _catInfo = getCategoriaInfo(item.categoria || categoriaAtiva[versao2.id]);
                                  return (
                                    <div
                                      key={idx}
                                      className="p-2 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded text-xs"
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-[color:var(--text-primary)] truncate">
                                            {item.fornecedor || item.nome || 'Sem nome'}
                                          </div>
                                          <div className="text-[color:var(--text-muted)] mt-0.5">
                                            {formatarDataSimples(item.data)}
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                          <div className={`font-semibold ${
                                            tipoInfo.cor === 'green' ? 'text-green-600' :
                                            tipoInfo.cor === 'red' ? 'text-red-600' : 'text-[color:var(--text-muted)]'
                                          }`}>
                                            {formatarMoeda(item.valor)}
                                          </div>
                                          <div className="flex gap-1">
                                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                                              tipoInfo.cor === 'green' ? 'bg-green-100 text-[color:var(--text-primary)]' :
                                              tipoInfo.cor === 'red' ? 'bg-red-100 text-red-700' : 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
                                            }`}>
                                              {tipoInfo.label}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="text-center text-xs text-[color:var(--text-muted)] py-4">
                                  Nenhuma conta nesta categoria
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Comparação Direta */}
                  <div className="mt-6 border-t pt-4">
                    <h5 className="text-sm font-medium text-[color:var(--text-primary)] mb-3">Diferenças entre as Versões:</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[color:var(--text-muted)]">Pendências API:</span>
                        <span className={`ml-2 font-semibold ${
                          versao1.qtd_pendencias_api_depois === versao2.qtd_pendencias_api_depois
                            ? 'text-[color:var(--text-muted)]'
                            : versao1.qtd_pendencias_api_depois < versao2.qtd_pendencias_api_depois
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {versao1.qtd_pendencias_api_depois} vs {versao2.qtd_pendencias_api_depois}
                        </span>
                      </div>
                      <div>
                        <span className="text-[color:var(--text-muted)]">Pendências OFX:</span>
                        <span className={`ml-2 font-semibold ${
                          versao1.qtd_pendencias_ofx_depois === versao2.qtd_pendencias_ofx_depois
                            ? 'text-[color:var(--text-muted)]'
                            : versao1.qtd_pendencias_ofx_depois < versao2.qtd_pendencias_ofx_depois
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {versao1.qtd_pendencias_ofx_depois} vs {versao2.qtd_pendencias_ofx_depois}
                        </span>
                      </div>
                      <div>
                        <span className="text-[color:var(--text-muted)]">Conciliações Automáticas:</span>
                        <span className={`ml-2 font-semibold ${
                          versao1.qtd_conciliadas_depois === versao2.qtd_conciliadas_depois
                            ? 'text-[color:var(--text-muted)]'
                            : versao1.qtd_conciliadas_depois > versao2.qtd_conciliadas_depois
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {versao1.qtd_conciliadas_depois} vs {versao2.qtd_conciliadas_depois}
                        </span>
                      </div>
                      <div>
                        <span className="text-[color:var(--text-muted)]">Conciliações Manuais:</span>
                        <span className={`ml-2 font-semibold ${
                          (versao1.conciliacoes_manuais?.length || 0) === (versao2.conciliacoes_manuais?.length || 0)
                            ? 'text-[color:var(--text-muted)]'
                            : (versao1.conciliacoes_manuais?.length || 0) > (versao2.conciliacoes_manuais?.length || 0)
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {versao1.conciliacoes_manuais?.length || 0} vs {versao2.conciliacoes_manuais?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalVersoesConciliacao;






